import React, { useMemo } from 'react';
import {
    Container,
    Grid,
    Title,
    List,
    Alert,
    Paper,
    Stack,
    Loader,
    Text,
    ThemeIcon,
} from '@mantine/core';
import {
    IconFolderOpen,
    IconClock,
    IconMap,
    IconFlame,
    IconTrophy,
    IconAlertCircle,
    IconInfoCircle,
} from '@tabler/icons-react';
import Subtitle from './Subtitle';
import useDemoMatches from 'hooks/useDemoMatches';
import { DemoMatch } from 'types/demos';
import { calculateDemoInsights } from 'utils/demoInsights';
import { DemoDownloadCard } from './DemoDownloadCard';

type TournamentStatus = 'live' | 'upcoming';

interface Tournament {
    id: string;
    name: string;
    status: TournamentStatus;
    description?: string;
    challongeUrl?: string;
    embedUrl?: string;
}

const iframeStyle: React.CSSProperties = {
    width: '100%',
    height: 500,
    border: 0
};

const iframeWrapperStyle: React.CSSProperties = {
    width: '100%',
    minHeight: 500
};

const sectionStyle: React.CSSProperties = {
    marginTop: 40,
};

const tournaments: Tournament[] = [
    {
        id: 'cs2',
        name: 'CS2',
        status: 'live',
        description: 'Counter-Strike 2 -turnauksen kaavio päivittyy reaaliajassa.',
        challongeUrl: 'https://challonge.com/fi/5kvh05qe',
        embedUrl: 'https://challonge.com/fi/5kvh05qe/module',
    },
    {
        id: 'tekken-3',
        name: 'Tekken 3',
        status: 'upcoming',
        description: 'Tekken 3 -turnauksen kaavio julkaistaan lähempänä tapahtumaa.',
    },
];

export const formatDateTime = (iso: string): string => {
    return new Date(iso).toLocaleString('fi-FI', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const TournamentSection: React.FC<{ tournament: Tournament }> = ({ tournament }) => {
    const { name, description, embedUrl, challongeUrl, status } = tournament;

    return (
        <section style={sectionStyle}>
            <Subtitle size="normal" title={name} id={`tournament-${tournament.id}`} />
            {description && <p>{description}</p>}
            {embedUrl ? (
                <div style={iframeWrapperStyle}>
                    <iframe
                        title={`${name} Challonge-bracket`}
                        src={embedUrl}
                        style={iframeStyle}
                        scrolling="auto"
                        frameBorder={0}
                    />
                </div>
            ) : (
                <p>
                    {status === 'upcoming'
                        ? 'Bracketti julkaistaan lähempänä turnausta. Palaathan tarkistamaan tämän sivun myöhemmin.'
                        : 'Bracketti ei ole saatavilla tällä hetkellä.'}
                </p>
            )}
            {challongeUrl && (
                <p>
                    Tarvitsetko erillisen näkymän?{' '}
                    <a href={challongeUrl} target="_blank" rel="noreferrer">
                        Avaa turnaus Challongessa
                    </a>
                    .
                </p>
            )}
        </section>
    );
};

export const HighlightList: React.FC<{ match: DemoMatch }> = ({ match }) => {
    if (!match.highlights?.length) {
        return null;
    }

    return (
        <List size="sm" withPadding>
            {match.highlights.map((highlight) => (
                <List.Item key={highlight.id}>
                    {highlight.player && <strong>{highlight.player}</strong>} ({highlight.type}) ·
                    R{highlight.round}: {highlight.description}
                </List.Item>
            ))}
        </List>
    );
};


const DemoInsightsPanel: React.FC<{
    insightsTotal: ReturnType<typeof calculateDemoInsights>;
}> = ({ insightsTotal }) => {
    return (
        <Paper shadow="sm" p="md" radius="md" withBorder style={{ borderLeft: '4px solid var(--mantine-color-violet-1)' }}>
            <Title order={4} mb="md">CS2-demoanalytiikka</Title>
            <List spacing="sm" center>
                <List.Item icon={<ThemeIcon color="violet" size={24} radius="xl"><IconFolderOpen size={16} /></ThemeIcon>}>
                    Tallenteita arkistossa: {insightsTotal.totalMatches}
                </List.Item>
                {typeof insightsTotal.avgDurationMinutes === 'number' && (
                    <List.Item icon={<ThemeIcon color="violet" size={24} radius="xl"><IconClock size={16} /></ThemeIcon>}>
                        Keskimääräinen kesto: {insightsTotal.avgDurationMinutes} min
                    </List.Item>
                )}
                {insightsTotal.mostPlayedMap && (
                    <List.Item icon={<ThemeIcon color="violet" size={24} radius="xl"><IconMap size={16} /></ThemeIcon>}>
                        Useimmin pelattu kartta: {insightsTotal.mostPlayedMap.map} (
                        {insightsTotal.mostPlayedMap.count}x)
                    </List.Item>
                )}
                {insightsTotal.hottestStreak && (
                    <List.Item icon={<ThemeIcon color="violet" size={24} radius="xl"><IconFlame size={16} /></ThemeIcon>}>
                        Kuumin voittoputki: {insightsTotal.hottestStreak.team}{' '}
                        ({insightsTotal.hottestStreak.wins} peräkkäistä)
                    </List.Item>
                )}
                {insightsTotal.recentWinners?.length > 0 && (
                    <List.Item icon={<ThemeIcon color="violet" size={24} radius="xl"><IconTrophy size={16} /></ThemeIcon>}>
                        Viimeisimmät voittajat:
                        <List size="sm" withPadding mt="xs">
                            {insightsTotal.recentWinners.map((entry) => (
                                <List.Item key={entry.matchId}>
                                    {entry.team} – {formatDateTime(entry.playedAt)}
                                </List.Item>
                            ))}
                        </List>
                    </List.Item>
                )}
            </List>
        </Paper>
    );
};

const DemoLibrarySection: React.FC = () => {
    const { matches, loading, error, refresh } = useDemoMatches();
    const csMatches = useMemo(
        () => matches.filter((match) => match.tournamentId === 'cs2'),
        [matches]
    );
    const insights = useMemo(() => calculateDemoInsights(csMatches), [csMatches]);

    return (
        <section style={sectionStyle}>
            <Subtitle size="normal" title="CS2-demoarkisto" id="demo-archive" />
            <p>
                Lataa Counter-Strike 2 -otteluiden <code>.dem</code>-tiedostot suoraan selaimesta. Tiedostot
                päivittyvät automaattisesti, kun ne lisätään tapahtuman demo-hakemistoon.
            </p>
            {error && (
                <Alert
                    color="red"
                    icon={<IconAlertCircle size={16} />}
                    title="Demoja ei saatu ladattua"
                    withCloseButton
                    onClose={refresh}
                    mb="md"
                >
                    {error}
                </Alert>
            )}
            <Grid gutter="md">
                <Grid.Col span={{ base: 12, md: 5 }}>
                    <DemoInsightsPanel insightsTotal={insights} />
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 7 }}>
                    {loading && (
                        <Paper shadow="sm" p="md" radius="md" withBorder>
                            <Title order={4}>
                                <Loader size="sm" mr="sm" /> Ladataan demoja...
                            </Title>
                        </Paper>
                    )}
                    {!loading && csMatches.length === 0 && (
                        <Alert color="blue" icon={<IconInfoCircle size={16} />}>
                            Demoarkisto on vielä tyhjä tältä turnaukselta.
                        </Alert>
                    )}
                    {!loading && csMatches.length > 0 && (
                        <Stack gap="md">
                            {csMatches.map((match) => (
                                <DemoDownloadCard key={match.id} match={match} />
                            ))}
                        </Stack>
                    )}
                </Grid.Col>
            </Grid>
        </section>
    );
};

const TournamentsPage: React.FC = () => {
    return (
        <Container size="md">
            <Paper shadow="sm" p="lg" radius="md" withBorder style={{ borderLeft: '4px solid var(--mantine-color-violet-0)' }}>
                <Subtitle size="big" title="Turnaukset" />
                <Text>
                    Bracketit ja ottelut päivittyvät tälle sivulle automaattisesti heti, kun turnaukset käynnistyvät.
                    Löydät alta kunkin turnauksen oman näkymän.
                </Text>
                {tournaments.map((tournament) => (
                    <TournamentSection key={tournament.id} tournament={tournament} />
                ))}
                <DemoLibrarySection />
            </Paper>
        </Container>
    );
};

export default TournamentsPage;
